import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail } from 'lucide-react';

export function Privacy() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-300">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link
          to="/signin"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#93c572]/10 rounded-full flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#93c572]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
            <p className="text-gray-500">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </div>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p className="mb-3">
              Pistachiorama ("we", "our", or "us") operates the Cognitive Workflow Engine demo application.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you use our service.
            </p>
            <p>
              We are committed to protecting your privacy and complying with applicable data protection
              laws, including the EU General Data Protection Regulation (GDPR), the California Consumer
              Privacy Act (CCPA), and other applicable privacy regulations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Data We Collect</h2>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">2.1 Account Information</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Email address</li>
              <li>Display name (from Google OAuth, if provided)</li>
              <li>Profile photo URL (from Google OAuth, if provided)</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">2.2 Usage Data</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Sign-in timestamps</li>
              <li>IP address (for security purposes)</li>
              <li>Authentication provider used</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">2.3 Local Storage</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Theme preferences</li>
              <li>Privacy consent status</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Data</h2>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-white">Authentication:</strong> To verify your identity and provide access to the service</li>
              <li><strong className="text-white">Security:</strong> To detect and prevent fraud, abuse, and unauthorized access</li>
              <li><strong className="text-white">Communication:</strong> To send you service-related notifications</li>
              <li><strong className="text-white">Improvement:</strong> To understand how our service is used and improve it</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Legal Basis for Processing (GDPR)</h2>
            <p className="mb-3">We process your personal data based on the following legal grounds:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-white">Consent:</strong> You have given consent for processing for specific purposes</li>
              <li><strong className="text-white">Contract:</strong> Processing is necessary to provide the service you requested</li>
              <li><strong className="text-white">Legitimate Interest:</strong> Processing for security and fraud prevention</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Third-Party Services</h2>
            <p className="mb-3">We use the following third-party services to operate our application:</p>

            <div className="bg-[#1a1a1a] rounded-lg p-4 space-y-3">
              <div>
                <h4 className="text-white font-medium">Firebase (Google)</h4>
                <p className="text-sm">Authentication services. <a href="https://firebase.google.com/support/privacy" className="text-[#93c572] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></p>
              </div>
              <div>
                <h4 className="text-white font-medium">Cloudflare</h4>
                <p className="text-sm">Hosting, database, and bot protection. <a href="https://www.cloudflare.com/privacypolicy/" className="text-[#93c572] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></p>
              </div>
              <div>
                <h4 className="text-white font-medium">Resend</h4>
                <p className="text-sm">Email delivery service. <a href="https://resend.com/legal/privacy-policy" className="text-[#93c572] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
            <p>
              We retain your personal data only for as long as necessary to provide the service and
              fulfill the purposes outlined in this policy. Sign-in logs are retained for 90 days
              for security purposes. You may request deletion of your data at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Your Rights</h2>
            <p className="mb-3">Under GDPR and similar privacy laws, you have the following rights:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-white">Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong className="text-white">Erasure:</strong> Request deletion of your personal data</li>
              <li><strong className="text-white">Portability:</strong> Receive your data in a portable format</li>
              <li><strong className="text-white">Objection:</strong> Object to processing of your data</li>
              <li><strong className="text-white">Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, visit your <Link to="/settings" className="text-[#93c572] hover:underline">account settings</Link> or
              contact us at the email below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Data Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside your country of
              residence, including the United States. We ensure appropriate safeguards are in place
              for international data transfers in compliance with applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal
              data against unauthorized access, alteration, disclosure, or destruction. This includes
              encryption, secure authentication, and regular security assessments.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Children's Privacy</h2>
            <p>
              Our service is not intended for children under 16 years of age. We do not knowingly
              collect personal data from children under 16.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Contact Us</h2>
            <p className="mb-3">
              If you have questions about this Privacy Policy or wish to exercise your data rights,
              please contact us:
            </p>
            <div className="bg-[#1a1a1a] rounded-lg p-4 flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#93c572]" />
              <a href="mailto:privacy@pistachiorama.ai" className="text-[#93c572] hover:underline">
                privacy@pistachiorama.ai
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
